import React, { useState, useEffect } from 'react';
import {
  KeyIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ClockIcon,
  UserIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { UserBrowserCredentialService } from '../../services/userBrowserCredentialService';
import { platformService, Platform } from '../../services/platformService';
import { usePreloadStore } from '../../store/preloadStore';
import PlatformLogo from './PlatformLogo';
import { getPlatformDisplayName } from '../../utils/platformUtils';

interface PlatformCredentialsTabProps {
  selectedPlatformId: number | null;
  onPlatformSelect: (platformId: number) => void;
  onError: (error: string | null) => void;
  onSuccess: (success: string | null) => void;
}

interface UserCredentialForm {
  [key: string]: string | File;
}

const PlatformCredentialsTab: React.FC<PlatformCredentialsTabProps> = ({
  selectedPlatformId,
  onPlatformSelect,
  onError,
  onSuccess
}) => {
  const [credentialForms, setCredentialForms] = useState<Record<number, UserCredentialForm>>({});
  const [showSecrets, setShowSecrets] = useState<Record<number, boolean>>({});
  const [saving, setSaving] = useState<Record<number, boolean>>({});
  const [checking, setChecking] = useState<Record<number, boolean>>({});
  const [credentialStatus, setCredentialStatus] = useState<Record<number, any>>({});

  // Get platforms from preload store
  const { getPlatforms } = usePreloadStore();
  const allPlatforms = getPlatforms();
  const platforms = allPlatforms.filter(platform =>
    platform.name.toLowerCase() !== 'zoom_users' && platform.requires_browser_automation
  );

  // Initialize forms when component mounts (only once)
  useEffect(() => {
    if (platforms.length > 0 && Object.keys(credentialForms).length === 0) {
      const initialForms: Record<number, UserCredentialForm> = {};
      platforms.forEach(platform => {
        const form: UserCredentialForm = {};
        if (platform.credential_fields && Object.keys(platform.credential_fields).length > 0) {
          Object.keys(platform.credential_fields).forEach(fieldKey => {
            form[fieldKey] = '';
          });
        } else {
          form.username = '';
          form.password = '';
        }
        initialForms[platform.id] = form;
      });
      setCredentialForms(initialForms);
    }
  }, []); // Empty dependency array - run only once on mount

  // Check credentials only when selectedPlatformId changes
  useEffect(() => {
    if (selectedPlatformId) {
      checkCredentials(selectedPlatformId);
    }
  }, [selectedPlatformId]);

  const checkCredentials = async (platformId: number) => {
    try {
      setChecking(prev => ({ ...prev, [platformId]: true }));
      const status = await UserBrowserCredentialService.checkCredentials(platformId);
      setCredentialStatus(prev => ({ ...prev, [platformId]: status }));
    } catch (error) {
      console.error(`Error checking credentials for platform ${platformId}:`, error);
      setCredentialStatus(prev => ({ ...prev, [platformId]: { exists: false } }));
    } finally {
      setChecking(prev => ({ ...prev, [platformId]: false }));
    }
  };

  const handleInputChange = (platformId: number, field: keyof UserCredentialForm, value: string | File) => {
    setCredentialForms(prev => ({
      ...prev,
      [platformId]: {
        ...prev[platformId],
        [field]: value
      }
    }));
  };

  const toggleSecretVisibility = (platformId: number) => {
    setShowSecrets(prev => ({
      ...prev,
      [platformId]: !prev[platformId]
    }));
  };

  const handleSave = async (platformId: number) => {
    const form = credentialForms[platformId];
    if (!form) return;

    try {
      setSaving(prev => ({ ...prev, [platformId]: true }));
      onError(null);
      onSuccess(null);

      // Check if at least one field has a value
      const hasCredentials = Object.values(form).some(value => {
        if (value instanceof File) return true;
        if (typeof value === 'string') return value.trim();
        return false;
      });
      if (!hasCredentials) {
        onError('Please fill in at least one credential field');
        return;
      }

      // Build credentials object and handle file uploads
      const formData = new FormData();
      const credentials: Record<string, any> = {};
      
      Object.entries(form).forEach(([key, value]) => {
        if (value instanceof File) {
          // File field - add to FormData
          formData.append('cert_file', value);
          credentials[key] = 'FILE_UPLOAD';  // Placeholder
        } else if (typeof value === 'string' && value.trim()) {
          credentials[key] = value.trim();
        }
      });

      formData.append('platform_id', platformId.toString());
      formData.append('credentials', JSON.stringify(credentials));

      // Use FormData API if we have file uploads, otherwise use regular JSON
      const hasFileUpload = Object.values(form).some(v => v instanceof File);
      
      if (hasFileUpload) {
        // Send as multipart/form-data using apiClient
        const { apiClient } = await import('../../services/apiClient');
        await apiClient.post('/api/store-user-browser-credentials/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        });
      } else {
        await UserBrowserCredentialService.storeCredentials(platformId, credentials);
      }

      onSuccess('Browser credentials stored successfully!');

      // Clear password/secret fields after successful save
      setCredentialForms(prev => {
        const updatedForm = { ...prev[platformId] };
        const platform = platforms.find(p => p.id === platformId);
        if (platform?.credential_fields) {
          Object.entries(platform.credential_fields).forEach(([key, config]: [string, any]) => {
            if (config.type === 'password') {
              updatedForm[key] = '';
            }
          });
        }
        return {
          ...prev,
          [platformId]: updatedForm
        };
      });

      // Refresh credential status
      const status = await UserBrowserCredentialService.checkCredentials(platformId);
      setCredentialStatus(prev => ({ ...prev, [platformId]: status }));

    } catch (err: any) {
      onError(err.message || 'Failed to store browser credentials');
    } finally {
      setSaving(prev => ({ ...prev, [platformId]: false }));
    }
  };

  const handleCheckCredentials = async (platformId: number) => {
    try {
      onError(null);
      await checkCredentials(platformId);
    } catch (err: any) {
      onError(err.message || 'Failed to check credentials');
    }
  };

  const handleDeleteCredentials = async (platformId: number) => {
    try {
      setSaving(prev => ({ ...prev, [platformId]: true }));
      onError(null);
      onSuccess(null);

      await UserBrowserCredentialService.deleteCredentials(platformId);

      onSuccess('Browser credentials deleted successfully!');

      // Clear form
      setCredentialForms(prev => {
        const clearedForm: UserCredentialForm = {};
        const platform = platforms.find(p => p.id === platformId);
        if (platform?.credential_fields && Object.keys(platform.credential_fields).length > 0) {
          Object.keys(platform.credential_fields).forEach(key => {
            clearedForm[key] = '';
          });
        } else {
          clearedForm.username = '';
          clearedForm.password = '';
        }
        return {
          ...prev,
          [platformId]: clearedForm
        };
      });

      // Update status
      setCredentialStatus(prev => ({ ...prev, [platformId]: { exists: false } }));

    } catch (err: any) {
      onError(err.message || 'Failed to delete credentials');
    } finally {
      setSaving(prev => ({ ...prev, [platformId]: false }));
    }
  };

  const selectedPlatform = platforms.find(p => p.id === selectedPlatformId);
  const selectedForm = selectedPlatformId ? credentialForms[selectedPlatformId] : null;
  const selectedStatus = selectedPlatformId ? credentialStatus[selectedPlatformId] : null;

  return (
    <div className="space-y-6">
      {/* Platform Details */}
      {selectedPlatform && selectedForm ? (
        <div className="p-6 rounded-lg border bg-theme-bg dark:bg-dark-theme-bg border-theme-border dark:border-dark-theme-border">
          <div className="flex items-center space-x-3 mb-6">
            <PlatformLogo
              platformName={selectedPlatform.name}
              size="lg"
              logoBase64={selectedPlatform.logo_base64}
            />
            <div>
              <h3 className="text-2xl font-semibold text-theme-text dark:text-dark-theme-text">
                {getPlatformDisplayName(selectedPlatform.name)} - User Credentials
              </h3>
              <div className="flex items-center space-x-2 mt-1">
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                  Browser Automation
                </span>
                {selectedStatus?.exists && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                    <CheckCircleIcon className="inline mr-1 w-3 h-3" />
                    Configured
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center mb-4 space-x-3">
            <UserIcon className="w-6 h-6 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
            <h4 className="text-xl font-semibold text-theme-text dark:text-dark-theme-text">
              Browser Login Credentials
            </h4>
          </div>

          <div className="space-y-4">
            {selectedPlatform.credential_fields && Object.keys(selectedPlatform.credential_fields).length > 0 ? (
              Object.entries(selectedPlatform.credential_fields)
                .sort(([keyA], [keyB]) => {
                  if (keyA.toLowerCase().includes('username') || keyA.toLowerCase().includes('email')) return -1;
                  if (keyB.toLowerCase().includes('username') || keyB.toLowerCase().includes('email')) return 1;
                  if (keyA.toLowerCase().includes('password')) return 1;
                  if (keyB.toLowerCase().includes('password')) return -1;
                  return 0;
                })
                .map(([fieldKey, fieldConfig]: [string, any]) => {
                  const isSecretField = fieldConfig.type === 'password';
                  const fieldValue = selectedForm[fieldKey] || '';
                  
                  return (
                    <div key={fieldKey}>
                      <div className="flex items-center mb-2 space-x-2">
                        <label className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary">
                          {fieldConfig.label}
                          {fieldConfig.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        {isSecretField && selectedStatus?.exists && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                            <CheckCircleIcon className="mr-1 w-3 h-3" />
                            Configured
                          </span>
                        )}
                      </div>
                    <div className="relative">
                      {fieldConfig.type === 'file' ? (
                        <input
                          type="file"
                          accept={fieldConfig.accept || '*'}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleInputChange(selectedPlatform.id, fieldKey, file);
                            }
                          }}
                          className="w-full px-4 py-2 rounded-md border border-theme-border dark:border-dark-theme-border bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent"
                          required={fieldConfig.required}
                        />
                      ) : (
                        <input
                          type={isSecretField && !showSecrets[selectedPlatform.id] ? 'password' : fieldConfig.type}
                          value={fieldValue}
                          onChange={(e) => handleInputChange(selectedPlatform.id, fieldKey, e.target.value)}
                          className={`w-full px-4 py-2 ${isSecretField ? 'pr-10' : ''} rounded-md border border-theme-border dark:border-dark-theme-border bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent`}
                          placeholder={
                            isSecretField && selectedStatus?.exists
                              ? `Update ${fieldConfig.label.toLowerCase()} (currently configured)`
                              : fieldConfig.placeholder
                          }
                          required={fieldConfig.required}
                        />
                      )}
                        {isSecretField && (
                          <button
                            type="button"
                            onClick={() => toggleSecretVisibility(selectedPlatform.id)}
                            className="flex absolute inset-y-0 right-0 items-center pr-3"
                            aria-label={showSecrets[selectedPlatform.id] ? `Hide ${fieldConfig.label}` : `Show ${fieldConfig.label}`}
                          >
                            {showSecrets[selectedPlatform.id] ? (
                              <EyeSlashIcon className="w-5 h-5 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
                            ) : (
                              <EyeIcon className="w-5 h-5 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
            ) : (
              <>
                {/* Fallback for platforms without credential_fields */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary">
                    Username / Email
                  </label>
                  <input
                    type="text"
                    value={selectedForm.username || ''}
                    onChange={(e) => handleInputChange(selectedPlatform.id, 'username', e.target.value)}
                    className="w-full px-4 py-2 rounded-md border border-theme-border dark:border-dark-theme-border bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent"
                    placeholder="Enter your username or email"
                  />
                </div>
                <div>
                  <div className="flex items-center mb-2 space-x-2">
                    <label className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary">
                      Password
                    </label>
                    {selectedStatus?.exists && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                        <CheckCircleIcon className="mr-1 w-3 h-3" />
                        Configured
                      </span>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type={showSecrets[selectedPlatform.id] ? 'text' : 'password'}
                      value={selectedForm.password || ''}
                      onChange={(e) => handleInputChange(selectedPlatform.id, 'password', e.target.value)}
                      className="w-full px-4 py-2 pr-10 rounded-md border border-theme-border dark:border-dark-theme-border bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark focus:border-transparent"
                      placeholder={
                        selectedStatus?.exists
                          ? "Update password (currently configured)"
                          : "Enter your password"
                      }
                    />
                    <button
                      type="button"
                      onClick={() => toggleSecretVisibility(selectedPlatform.id)}
                      className="flex absolute inset-y-0 right-0 items-center pr-3"
                      aria-label={showSecrets[selectedPlatform.id] ? "Hide password" : "Show password"}
                    >
                      {showSecrets[selectedPlatform.id] ? (
                        <EyeSlashIcon className="w-5 h-5 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
                      ) : (
                        <EyeIcon className="w-5 h-5 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-theme-text-tertiary dark:text-dark-theme-text-tertiary">
                    These credentials are stored securely and used only for browser automation.
                    {selectedStatus?.exists && (
                      <span className="block mt-1">
                        • Existing password is hidden for security. Enter a new value to update.
                      </span>
                    )}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between mt-6">
            {selectedStatus?.exists && (
              <button
                onClick={() => handleDeleteCredentials(selectedPlatform.id)}
                disabled={saving[selectedPlatform.id]}
                className="flex items-center px-4 py-2 space-x-2 text-white bg-red-600 rounded-md transition-opacity duration-150 dark:bg-red-700 hover:opacity-90 disabled:opacity-50"
              >
                <XMarkIcon className="w-4 h-4" />
                <span>
                  {saving[selectedPlatform.id] ? 'Deleting...' : 'Delete Credentials'}
                </span>
              </button>
            )}
            <div className="flex space-x-3 ml-auto">
              <button
                onClick={() => handleSave(selectedPlatform.id)}
                disabled={saving[selectedPlatform.id]}
                className="flex items-center px-6 py-2 space-x-2 text-white rounded-md transition-opacity duration-150 bg-primary-light dark:bg-primary-dark hover:opacity-90 disabled:opacity-50"
              >
                <LockClosedIcon className="w-4 h-4" />
                <span>
                  {saving[selectedPlatform.id] ? 'Saving...' : 'Save Credentials'}
                </span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <UserIcon className="mx-auto mb-4 w-16 h-16 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
            <p className="text-theme-text-secondary dark:text-dark-theme-text-secondary">
              Select a platform to manage your browser credentials
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformCredentialsTab;